import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Download,
  Filter,
  Store,
  RefreshCcw,
  Loader2,
  Calendar,
  Printer as PrintIcon,
  CheckCircle,
  Activity,
  FileText,
  DollarSign,
  Receipt,
  PieChart,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const ProfitLossReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    store_id: currentStore?.id ? String(currentStore.id) : "",
    start_date: "",
    end_date: "",
  }));
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);

  const [storeSearch, setStoreSearch] = useState("");
  const [debouncedStoreSearch, setDebouncedStoreSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStoreSearch(storeSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [storeSearch]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ==================== QUERIES ====================
  const { data: stores = [], isFetching: isStoresFetching } = useQuery({
    enabled: hasPermission("report profit loss view"),
    queryKey: ["stores", debouncedStoreSearch],
    queryFn: async () => {
      const params = {};
      if (debouncedStoreSearch) {
        params.search = debouncedStoreSearch;
      }
      const response = await apiClient.get(`/store`, {
        params,
      });
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report profit loss view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.PROFIT_LOSS,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report profit loss view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.PROFIT_LOSS,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Profit & Loss report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate profit & loss report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating profit & loss report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      store_id: currentStore?.id ? String(currentStore.id) : "",
      start_date: "",
      end_date: "",
    });
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `profit-loss-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Activity className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Financial Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Profit & Loss Report
              </span>
            </div>
          }
          subtitle="Generate and analyze comprehensive profit and loss statements."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report profit loss view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report profit loss view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Report Parameters</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Limit results by dates and locations
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-8">
              {/* Date Filters */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-4">
                  <Calendar className="h-4 w-4 text-primary" /> Date Range
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      name="start_date"
                      value={filters.start_date}
                      onChange={handleFilterChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      name="end_date"
                      value={filters.end_date}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Store className="h-4 w-4 text-primary" /> Location &
                    Entities
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Warehouse / Store
                      </Label>
                      <ReactSelect
                        options={storeOptions}
                        isLoading={isStoresFetching}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onInputChange={(val, { action }) => {
                          if (action === "input-change") {
                            setStoreSearch(val);
                          }
                        }}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {previewData.summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Net Revenue
                      </p>
                      <h3 className="text-2xl font-bold text-primary">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_net_revenue) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <Receipt className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total COGS
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_cogs) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-muted text-muted-foreground rounded-xl">
                      <DollarSign className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Gross Profit
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.gross_profit) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                      <Activity className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Net Profit
                      </p>
                      <h3
                        className={`text-2xl font-bold ${parseFloat(previewData.summary.net_profit) >= 0 ? "text-success" : "text-destructive"}`}
                      >
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.net_profit) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div
                      className={`p-3 rounded-xl ${parseFloat(previewData.summary.net_profit) >= 0 ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}
                    >
                      {parseFloat(previewData.summary.net_profit) >= 0 ? (
                        <TrendingUp className="h-6 w-6" />
                      ) : (
                        <TrendingDown className="h-6 w-6" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {previewData.data &&
              previewData.data.map((storeData, index) => (
                <Card
                  key={index}
                  className="shadow-none border border-border/50 overflow-hidden bg-background"
                >
                  <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Store className="h-5 w-5 text-primary" />{" "}
                        {storeData.store_name}
                      </CardTitle>
                      <CardDescription>
                        Profit and Loss Statement from{" "}
                        {storeData.start_date || "N/A"} to{" "}
                        {storeData.end_date || "N/A"}
                      </CardDescription>
                    </div>
                    <div className="flex gap-4 items-center">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          Net Profit
                        </p>
                        <p
                          className={`font-bold text-lg ${parseFloat(storeData.net_profit) >= 0 ? "text-success" : "text-destructive"}`}
                        >
                          {currencySymbol}
                          {(
                            parseFloat(storeData.net_profit) || 0
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-8">
                    {/* Revenue Section */}
                    <div>
                      <h4 className="font-semibold text-lg border-b pb-2 mb-4">
                        Revenue
                      </h4>
                      <div className="space-y-2">
                        {storeData.revenue.map((rev, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-sm"
                          >
                            <span>{rev.description}</span>
                            <span>
                              {(rev.credit || 0) > 0
                                ? ""
                                : (rev.debit || 0) > 0
                                  ? "-"
                                  : ""}{" "}
                              {currencySymbol}
                              {(
                                parseFloat(rev.credit || rev.debit) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center font-bold text-sm pt-2 border-t mt-2">
                          <span>Net Revenue</span>
                          <span>
                            {currencySymbol}
                            {(
                              parseFloat(storeData.net_revenue) || 0
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* COGS Section */}
                    <div>
                      <h4 className="font-semibold text-lg border-b pb-2 mb-4">
                        Cost of Goods Sold (COGS)
                      </h4>
                      <div className="space-y-2">
                        {storeData.cogs.map((cog, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-sm"
                          >
                            <span>{cog.description}</span>
                            <span>
                              {currencySymbol}
                              {(parseFloat(cog.amount) || 0).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 },
                              )}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center font-bold text-sm pt-2 border-t mt-2">
                          <span>Total COGS</span>
                          <span>
                            {currencySymbol}
                            {(
                              parseFloat(storeData.total_cogs) || 0
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Gross Profit Section */}
                    <div className="flex justify-between items-center font-bold text-base p-3 bg-muted/30 rounded-lg border">
                      <span>Gross Profit</span>
                      <span>
                        {currencySymbol}
                        {(
                          parseFloat(storeData.gross_profit) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>

                    {/* Expenses Section */}
                    <div>
                      <h4 className="font-semibold text-lg border-b pb-2 mb-4">
                        Operating Expenses
                      </h4>
                      <div className="space-y-2">
                        {storeData.expenses && storeData.expenses.length > 0 ? (
                          storeData.expenses.map((exp, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-sm"
                            >
                              <span>{exp.category || exp.description}</span>
                              <span>
                                {currencySymbol}
                                {(parseFloat(exp.amount) || 0).toLocaleString(
                                  undefined,
                                  { minimumFractionDigits: 2 },
                                )}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            No operating expenses recorded.
                          </p>
                        )}
                        <div className="flex justify-between items-center font-bold text-sm pt-2 border-t mt-2">
                          <span>Total Expenses</span>
                          <span>
                            {currencySymbol}
                            {(
                              parseFloat(storeData.total_expenses) || 0
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Net Profit Section */}
                    <div
                      className={`flex justify-between items-center font-bold text-lg p-4 rounded-lg border ${parseFloat(storeData.net_profit) >= 0 ? "bg-success/5 border-success/20 text-success" : "bg-destructive/5 border-destructive/20 text-destructive"}`}
                    >
                      <span>Net Profit</span>
                      <span>
                        {currencySymbol}
                        {(parseFloat(storeData.net_profit) || 0).toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2 },
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Compiled!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Your Profit & Loss report has been successfully generated and
                  is ready for download or printing.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

